import React, { useState } from 'react'
import * as LucideIcons from 'lucide-react'
import { Button, Input, Select } from '../common'
import useCategoryStore from '../../stores/categoryStore'
import { AVAILABLE_ICONS } from '../../utils/constants'

// Section options for custom categories
const SECTION_OPTIONS = [
  { value: '', label: 'Standalone (after G section)' },
  { value: 'D', label: 'D - Outside condition' },
  { value: 'E', label: 'E - Inside condition' },
  { value: 'F', label: 'F - Services' },
  { value: 'G', label: 'G - Grounds' }
]

// Map icon names to Lucide components
const getIcon = (iconName) => {
  const iconMap = {
    'home': LucideIcons.Home,
    'trees': LucideIcons.Trees,
    'warehouse': LucideIcons.Warehouse,
    'utensils': LucideIcons.Utensils,
    'sofa': LucideIcons.Sofa,
    'bed-double': LucideIcons.BedDouble,
    'bed': LucideIcons.Bed,
    'bath': LucideIcons.Bath,
    'shower-head': LucideIcons.ShowerHead,
    'door-open': LucideIcons.DoorOpen,
    'flower-2': LucideIcons.Flower2,
    'car': LucideIcons.Car,
    'gauge': LucideIcons.Gauge,
    'folder': LucideIcons.Folder,
    'camera': LucideIcons.Camera,
    'image': LucideIcons.Image,
    'building': LucideIcons.Building,
    'hammer': LucideIcons.Hammer,
    'thermometer': LucideIcons.Thermometer,
    'droplets': LucideIcons.Droplets
  }
  return iconMap[iconName] || LucideIcons.Folder
}

export default function CategoryForm({ category, onClose }) {
  const addCategory = useCategoryStore(state => state.addCategory)
  const updateCategory = useCategoryStore(state => state.updateCategory)
  const loading = useCategoryStore(state => state.loading)

  const [formData, setFormData] = useState({
    name: category?.name || '',
    icon: category?.icon || 'folder',
    section: category?.section || '',
    aiPrompt: category?.aiPrompt || ''
  })

  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) return

    try {
      if (category?.id) {
        await updateCategory(category.id, formData)
      } else {
        await addCategory(formData)
      }
      onClose()
    } catch (error) {
      console.error('Failed to save category:', error)
      setErrors({ submit: 'Failed to save category. Please try again.' })
    }
  }

  const SelectedIcon = getIcon(formData.icon)

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Category Name"
        name="name"
        placeholder="e.g., Kitchen, Bathroom, Roof"
        value={formData.name}
        onChange={handleChange}
        error={errors.name}
      />

      {/* Icon selector */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Icon
        </label>
        <div className="flex items-center gap-4 mb-3">
          <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center">
            <SelectedIcon className="w-8 h-8 text-primary-700" />
          </div>
          <div className="flex-1">
            <Select
              name="icon"
              value={formData.icon}
              onChange={handleChange}
              options={AVAILABLE_ICONS}
              placeholder="Select an icon"
            />
          </div>
        </div>

        {/* Icon preview grid */}
        <div className="grid grid-cols-5 sm:grid-cols-10 gap-2 p-3 bg-slate-50 rounded-lg">
          {AVAILABLE_ICONS.map((iconOption) => {
            const IconComponent = getIcon(iconOption.value)
            const isSelected = formData.icon === iconOption.value

            return (
              <button
                key={iconOption.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, icon: iconOption.value }))}
                className={`
                  p-2 rounded-lg transition-all
                  ${isSelected
                    ? 'bg-primary-700 text-white shadow-md'
                    : 'bg-white text-slate-600 hover:bg-primary-50 hover:text-primary-700 border border-slate-200'
                  }
                `}
                title={iconOption.label}
              >
                <IconComponent className="w-5 h-5 mx-auto" />
              </button>
            )
          })}
        </div>
      </div>

      {/* Section selector (only for new custom categories) */}
      {!category?.isStatic && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">
            Section Placement
          </label>
          <Select
            name="section"
            value={formData.section}
            onChange={handleChange}
            options={SECTION_OPTIONS}
          />
          <p className="text-sm text-slate-500 mt-1">
            {formData.section
              ? `Category will appear at the end of Section ${formData.section}`
              : 'Category will appear as a standalone page after Section G'}
          </p>
        </div>
      )}

      {/* AI Prompt (basic) */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          AI Prompt (Optional)
        </label>
        <textarea
          name="aiPrompt"
          value={formData.aiPrompt}
          onChange={handleChange}
          placeholder="Enter the prompt that AI will use to analyse photos in this category..."
          rows={4}
          className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 resize-none"
        />
        <p className="text-sm text-slate-500 mt-1">
          This prompt will be sent to ChatGPT when analysing photos in this category.
        </p>
      </div>

      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{errors.submit}</p>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          className="flex-1"
        >
          {category ? 'Save Changes' : 'Add Category'}
        </Button>
      </div>
    </form>
  )
}
